import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom/client';
import {Provider} from "react-redux";
import {PersistGate} from "redux-persist/integration/react";
import {createBrowserRouter, Navigate, RouterProvider} from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'

import './index.css';
import {persistor, store} from "./store/store";
import {MAIN_ROUTES} from "./routing/main_routes";
import MainLayout from "./layouts/MainLayout/MainLayout";
import TitleManager from "./components/TitleManager/TitleManager";
import MainPage from "./pages/MainPage/MainPage";
import CreateOrderLayout from "./layouts/CreateOrderLayout/CreateOrderLayout";
import LocationPage from "./pages/StepperPages/LocationPage/LocationPage";
import ChoosePackagePage from "./pages/StepperPages/ChoosePackagePage/ChoosePackagePage";
import DetailsPackagePage from "./pages/StepperPages/DetailsPackagePage/DetailsPackagePage";
import DateTimeOrderPage from "./pages/StepperPages/DateTimeOrderPage/DateTimeOrderPage";
import InfoAboutCustomerPage from "./pages/StepperPages/InfoAboutCustomerPage/InfoAboutCustomerPage";
import HistoryOrderPage from "./pages/HistoryOrderPage/HistoryOrderPage";
import HistoryOrderDetailsPage from "./pages/HistoryOrderDetailsPage/HistoryOrderDetailsPage";
import SettingsPageCleaner from "./pages/SettingsPageСleaner/SettingsPageСleaner";
import SettingsPageCustomer from "./pages/SettingsPageCustomer/SettingsPageCustomer";
import TriangleStepperPagesContainer from "./pages/TriangleStepperPagesContainer/TriangleStepperPagesContainer";
import PaymentPage from "./pages/PaymentPage/PaymentPage";
import ApplyForOrder from "./pages/ApplyForOrder/ApplyForOrder";
import AcceptOrderPage from "./pages/AcceptOrderPage/AcceptOrderPage";
import ErrorPage from "./pages/ErrorPage/ErrorPage";
import RegistrationPageCustomer from "./pages/RegistrationPageCustomer/RegistrationPageCustomer";
import RegistrationPageCleaner
  from "./pages/RegistrationPages/RegistrationPagesCleaner/RegistrationPageCleaner/RegistrationPageCleaner";
import LoginPage from "./pages/LoginPage/LoginPage";
import ForgotPasswordStep1 from "./pages/ForgotPasswordStep1/ForgotPasswordStep1";
import ForgotPasswordStep2 from "./pages/ForgotPasswordStep2/ForgotPasswordStep2";
import ForgotPasswordStep3 from "./pages/ForgotPasswordStep3/ForgotPasswordStep3";
import ForgotPasswordStep4 from "./pages/ForgotPasswordStep4/ForgotPasswordStep4";
import FeedbackPageCleaner from "./pages/FeedbackPageCleaner/FeedbackPageCleaner";
import FeedbackPageCustomer from "./pages/FeedbackPageCustomer/FeedbackPageCustomer";
import OffersCleanerPage from "./pages/OffersCleanerPage/OffersCleanerPage";
import ActiveOrdersCleanerPage from "./pages/ActiveOrdersCleanerPage/ActiveOrdersCleanerPage";
import AllCleaningRequests from "./pages/AllCleaningRequests/AllCleaningRequests";
import AllAppliedCleanerApplicationsPage
  from "./pages/AllAppliedCleanerApplicationsPage/AllAppliedCleanerApplicationsPage";
import QrCodePhotos from "./components/QrCodePhotos/QrCodePhotos";
import AppliedApplicationDetailsPage from "./pages/AppliedApplicationDetailsPage/AppliedApplicationDetailsPage";
import SupportTicketsListPage from "./pages/SupportTicketsListPage/SupportTicketsListPage";
import SupportChatPage from "./pages/SupportChatPage/SupportChatPage";
import {Helmet} from "react-helmet";

const App = () => {
  const [isCleaner, setIsCleaner] = useState(!!JSON.parse(localStorage.getItem('isCleaner')));
  const [currentStep, setCurrentStep] = useState(JSON.parse(localStorage.getItem('currentStep')));
  const [isRemembered, setIsRemembered] = useState(!!JSON.parse(localStorage.getItem('isRemembered')) || false);
  const [isPageClosed, setIsPageClosed] = useState(!!JSON.parse(sessionStorage.getItem('isRemembered') || (!isRemembered)));

  console.error(!!JSON.parse(sessionStorage.getItem('isRemembered') || (!isRemembered)))
  let isTokenValid = false;

  const isAuthorized = !!JSON.parse(localStorage.getItem('token'))?.access;
  const tokenExpires = JSON.parse(localStorage.getItem('expires'));
  if (tokenExpires?.iat < tokenExpires?.exp) {
    isTokenValid = true;
  }

  if (!currentStep) {
    setCurrentStep(1);
    localStorage.setItem('currentStep', JSON.stringify(1));
  }

  const router = createBrowserRouter([
    {
      // Pathless layout route: keeps the browser-tab title in sync with the
      // route on every navigation (renders <Outlet/> for the real routes).
      element: <TitleManager/>,
      children: [
    {
      index: true,
      element: (isAuthorized && isTokenValid) ? (
        isCleaner ? <Navigate to={MAIN_ROUTES.ALL_CLEANING_REQUESTS}/> : <Navigate to={MAIN_ROUTES.MAIN}/>
      ) : (
        <Navigate to={MAIN_ROUTES.CREATE_ORDER}/>
      )
    },
    {
      path: '',
      element: <MainLayout/>,
      children: [
        {
          path: MAIN_ROUTES.MAIN,
          element: <MainPage/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: MAIN_ROUTES.CREATE_ORDER,
          element: <CreateOrderLayout setCurrentStep={setCurrentStep}/>,
          // errorElement: <ErrorPage/>,
          children: [
            {
              index: true,
              element: (!!currentStep && currentStep !== 5) ?
                <Navigate to={`/create-order/step-${currentStep}`}/> :
                <Navigate to={MAIN_ROUTES.CREATE_ORDER_STEP_1}/>,
              // errorElement: <ErrorPage/>,
            },
            {
              path: MAIN_ROUTES.CREATE_ORDER_STEP_1,
              element: <LocationPage setCurrentStep={setCurrentStep}/>,
              // errorElement: <ErrorPage/>,
            },
            {
              path: MAIN_ROUTES.CREATE_ORDER_STEP_2,
              element: <ChoosePackagePage setCurrentStep={setCurrentStep}/>,
              // errorElement: <ErrorPage/>,
            },
            {
              path: MAIN_ROUTES.CREATE_ORDER_STEP_3,
              element: <DetailsPackagePage setCurrentStep={setCurrentStep}/>,
              // errorElement: <ErrorPage/>,
            },
            {
              path: MAIN_ROUTES.CREATE_ORDER_STEP_4,
              element: <DateTimeOrderPage setCurrentStep={setCurrentStep}/>,
              // errorElement: <ErrorPage/>,
            },
            {
              path: MAIN_ROUTES.CREATE_ORDER_STEP_5,
              element: <InfoAboutCustomerPage setCurrentStep={setCurrentStep}/>,
              // errorElement: <ErrorPage/>,
            },
          ]
        },
        {
          path: MAIN_ROUTES.COMPLETED_ORDERS,
          element: <HistoryOrderPage/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: `${MAIN_ROUTES.COMPLETED_ORDERS}/:id`,
          element: <HistoryOrderDetailsPage/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: MAIN_ROUTES.SETTINGS,
          element: isCleaner ? <SettingsPageCleaner/> : <SettingsPageCustomer/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: `${MAIN_ROUTES.ORDER_DETAILS}/:id`,
          element: <TriangleStepperPagesContainer/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: MAIN_ROUTES.PAYMENT,
          element: <PaymentPage/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: MAIN_ROUTES.ALL_CLEANING_REQUESTS,
          element: <AllCleaningRequests/>,
          // errorElement: <ErrorPage/>
        },
        // {
        //   path: MAIN_ROUTES.OFFERS,
        //   element: <OffersCleanerPage/>,
        //   // errorElement: <ErrorPage/>
        // },
        {
          path: MAIN_ROUTES.ORDERS,
          element: <ActiveOrdersCleanerPage/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: `${MAIN_ROUTES.CLEANER_APPLICATION}/:id`,
          element: <AppliedApplicationDetailsPage/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: `${MAIN_ROUTES.APPLY_FOR_ORDER}/:id`,
          element: <ApplyForOrder/>,
          // errorElement: <ErrorPage/>
        },
        // {
        //   path: `${MAIN_ROUTES.ACCEPT_ORDER}/:id`,
        //   element: <AcceptOrderPage/>,
        //   // errorElement: <ErrorPage/>
        // },
        {
          path: MAIN_ROUTES.CLEANER_APPLICATIONS,
          element: <AllAppliedCleanerApplicationsPage/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: `${MAIN_ROUTES.ACTIVE_ORDER}/:id`,
          element: <AcceptOrderPage/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: MAIN_ROUTES.FEEDBACK,
          element: isCleaner ? <FeedbackPageCleaner/> : <FeedbackPageCustomer/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: MAIN_ROUTES.SUPPORT,
          element: <SupportTicketsListPage/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: `${MAIN_ROUTES.SUPPORT_CHAT}/:id`,
          element: <SupportChatPage/>,
          // errorElement: <ErrorPage/>
        },
        {
          path: '*',
          element: <ErrorPage/>,
          // errorElement: <ErrorPage/>
        },
      ]
    },
    {
      path: MAIN_ROUTES.REGISTRATION_CUSTOMER,
      element: <RegistrationPageCustomer/>,
      // errorElement: <ErrorPage/>
    },
    {
      path: MAIN_ROUTES.REGISTRATION_CLEANER,
      element: <RegistrationPageCleaner setIsCleaner={setIsCleaner}/>,
      // errorElement: <ErrorPage/>
    },
    {
      path: MAIN_ROUTES.LOGIN,
      element: <LoginPage setIsCleaner={setIsCleaner} setIsRemembered={setIsRemembered}
                          isRemembered={isRemembered}/>,
      // errorElement: <ErrorPage/>
    },
    {
      path: MAIN_ROUTES.FORGOT_PASSWORD_STEP_1,
      element: <ForgotPasswordStep1/>,
      // errorElement: <ErrorPage/>
    },
    {
      path: MAIN_ROUTES.FORGOT_PASSWORD_STEP_2,
      element: <ForgotPasswordStep2/>,
      // errorElement: <ErrorPage/>
    },
    {
      path: `${MAIN_ROUTES.FORGOT_PASSWORD_STEP_3}/:token`,
      element: <ForgotPasswordStep3/>,
      // errorElement: <ErrorPage/>
    },
    {
      path: MAIN_ROUTES.FORGOT_PASSWORD_STEP_4,
      element: <ForgotPasswordStep4/>,
      // errorElement: <ErrorPage/>
    },
    {
      path: '*',
      element: <ErrorPage/>,
      errorElement: <ErrorPage/>
    },
    {
      path: 'qr/:session_id',
      element: <QrCodePhotos/>,
      errorElement: <ErrorPage/>
    }
      ]
    }
  ])

  const queryClient = new QueryClient();

  // to clear local storage when the user does not remember the data
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!isRemembered) {
        if (isPageClosed) {
          // localStorage.clear();
          // window.location.reload();
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isRemembered, isPageClosed]);


  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <Helmet>
            <script>
              {`(function (h, o, t, j, a, r) {
                h.hj = h.hj || function () {
                  (h.hj.q = h.hj.q || []).push(arguments)
                };
                h._hjSettings = {hjid: 4958030, hjsv: 6};
                a = o.getElementsByTagName('head')[0];
                r = o.createElement('script');
                r.async = 1;
                r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
                a.appendChild(r);
              })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=')`}
            </script>
          </Helmet>
          <RouterProvider router={router}/>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
